// Filters, sorteringen en weergaven van de Trends-tab op de trendlaag. Dezelfde
// vorm als de huidige Trends-tab (nieuw/trends/data.ts: assen met meerkeuze,
// sorteringen met één zin uitleg), vertaald naar
// trend › ontwikkeling. Alles rekent in de browser op GET /api/trendlaag/trends.

import type { TrendlaagTrend } from "../../lib/api";
import { heeftWet } from "./woorden";

export type Soort = "regel" | "werkvloer" | "onderwijs" | "markt";
export const SOORTEN: { waarde: Soort; label: string; uitleg: string }[] = [
  { waarde: "regel", label: "Regel", uitleg: "Wetten, normen en afspraken die het werk veranderen." },
  { waarde: "werkvloer", label: "Werkvloer", uitleg: "Wat er in het dagelijkse werk anders gaat." },
  { waarde: "onderwijs", label: "Onderwijs", uitleg: "Wat opleiders en scholen veranderen." },
  { waarde: "markt", label: "Markt", uitleg: "Vraag, aanbod en arbeidsmarkt." },
];

/** De bewegingslabels van de API, in deze volgorde in het menu. */
export const BEWEGINGEN = ["groeit", "stabiel", "minder recent bewijs", "te weinig"] as const;

export type Kenmerk = "wet" | "opkomend";
export const KENMERKEN: { waarde: Kenmerk; label: string; uitleg: string }[] = [
  { waarde: "wet", label: "Wet", uitleg: "Een regel met een genoemde datum." },
  { waarde: "opkomend", label: "Opkomend", uitleg: "De ontwikkeling is opkomend." },
];
/** Plan-modus filtert nog per trend (rij 10 laat die ongemoeid). */
export const KENMERKEN_TREND: { waarde: Kenmerk; label: string; uitleg: string }[] = [
  { waarde: "wet", label: "Wet", uitleg: "Met minstens één regel met een genoemde datum." },
  { waarde: "opkomend", label: "Opkomend", uitleg: "De trend of een van zijn ontwikkelingen is opkomend." },
];

export type Filters = {
  onderwerp: string[];
  soort: Soort[];
  beweging: string[];
  kenmerk: Kenmerk[];
};
export const LEGE_FILTERS: Filters = { onderwerp: [], soort: [], beweging: [], kenmerk: [] };
export const aantalFilters = (f: Filters): number =>
  f.onderwerp.length + f.soort.length + f.beweging.length + f.kenmerk.length;

/** De soort van de trend of van een van zijn ontwikkelingen. */
export const heeftSoort = (t: TrendlaagTrend, s: Soort): boolean =>
  t.ontwikkelingen.some((o) => o.soort === s) || (s === "markt" && t.soortTrend === "arbeidsmarkt");

export const isOpkomend = (t: TrendlaagTrend): boolean => t.opkomend || t.ontwikkelingen.some((o) => o.opkomend);

const KENMERK: Record<Kenmerk, (t: TrendlaagTrend) => boolean> = { wet: heeftWet, opkomend: isOpkomend };

/** Past de trend op één as? Binnen een as is het of, tussen de assen en. */
function pastOp(t: TrendlaagTrend, f: Filters, as: keyof Filters): boolean {
  switch (as) {
    case "onderwerp":
      return !f.onderwerp.length || f.onderwerp.includes(t.onderwerp);
    case "soort":
      return !f.soort.length || f.soort.some((s) => heeftSoort(t, s));
    case "beweging":
      return !f.beweging.length || (t.bewegingLabel != null && f.beweging.includes(t.bewegingLabel));
    case "kenmerk":
      return f.kenmerk.every((k) => KENMERK[k](t));
  }
}

const ASSEN: (keyof Filters)[] = ["onderwerp", "soort", "beweging", "kenmerk"];

/** Past de trend op alle assen, eventueel op één na (voor de tellingen in een menu). */
export const past = (t: TrendlaagTrend, f: Filters, behalve?: keyof Filters): boolean =>
  ASSEN.every((as) => as === behalve || pastOp(t, f, as));

/** Zoeken in de titel, het onderwerp en de titels van de ontwikkelingen. */
export function zoekTreft(t: TrendlaagTrend, zoek: string): boolean {
  const q = zoek.trim().toLowerCase();
  if (!q) return true;
  return (
    t.titel.toLowerCase().includes(q) ||
    t.onderwerp.toLowerCase().includes(q) ||
    t.ontwikkelingen.some((o) => o.titel.toLowerCase().includes(q))
  );
}

/** Tellingen per waarde op één as, binnen wat de andere assen en het zoeken overlaten. */
export function tel(trends: readonly TrendlaagTrend[], f: Filters, as: keyof Filters, waarde: (t: TrendlaagTrend) => string[]): Record<string, number> {
  const uit: Record<string, number> = {};
  for (const t of trends) if (past(t, f, as)) for (const w of waarde(t)) uit[w] = (uit[w] ?? 0) + 1;
  return uit;
}

// ── Per ontwikkeling (testfeedback 03-11, rij 10; owner koos C) ────────────
// Onderwerp en beweging horen bij de trend; soort, Wet en opkomend horen bij
// de ontwikkeling zelf. "Regel" geeft dus de ontwikkelingen die een regel zijn,
// niet alles onder een trend waar ergens een regel in zit.

export type Stand = "trends" | "ontwikkelingen";
export type Ontwikkeling = TrendlaagTrend["ontwikkelingen"][number];

const ONTW_KENMERK: Record<Kenmerk, (o: Ontwikkeling) => boolean> = { wet: (o) => !!o.regelMetDatum, opkomend: (o) => !!o.opkomend };

/** Zoeken: past de trend zelf, dan alles eronder; anders de titel van de ontwikkeling. */
function zoekTreftOntw(t: TrendlaagTrend, o: Ontwikkeling, zoek: string): boolean {
  const q = zoek.trim().toLowerCase();
  if (!q) return true;
  return t.titel.toLowerCase().includes(q) || t.onderwerp.toLowerCase().includes(q) || o.titel.toLowerCase().includes(q);
}

/** Past de ontwikkeling op soort, Wet/opkomend en het zoekwoord (eventueel op één as na). */
export function ontwPast(t: TrendlaagTrend, o: Ontwikkeling, f: Filters, zoek: string, behalve?: keyof Filters): boolean {
  const soortOk =
    behalve === "soort" || !f.soort.length || f.soort.some((s) => o.soort === s || (s === "markt" && t.soortTrend === "arbeidsmarkt"));
  const kenmerkOk = behalve === "kenmerk" || f.kenmerk.every((k) => ONTW_KENMERK[k](o));
  return soortOk && kenmerkOk && zoekTreftOntw(t, o, zoek);
}

/** Staat er een filter of zoekwoord dat ontwikkelingen uitkiest? */
export const ontwFilterAan = (f: Filters, zoek: string): boolean => f.soort.length > 0 || f.kenmerk.length > 0 || !!zoek.trim();

/** De ontwikkelingen van een trend die passen. */
export const passendeOntw = (t: TrendlaagTrend, f: Filters, zoek: string): Ontwikkeling[] =>
  t.ontwikkelingen.filter((o) => ontwPast(t, o, f, zoek));

/** Tellingen per waarde op één as, in ontwikkelingen: binnen wat de andere
 *  assen en het zoeken overlaten. */
export function telOntw(
  trends: readonly TrendlaagTrend[],
  f: Filters,
  zoek: string,
  as: keyof Filters,
  waarde: (t: TrendlaagTrend, o: Ontwikkeling) => string[],
): Record<string, number> {
  const uit: Record<string, number> = {};
  for (const t of trends) {
    if (as !== "onderwerp" && !pastOp(t, f, "onderwerp")) continue;
    if (as !== "beweging" && !pastOp(t, f, "beweging")) continue;
    for (const o of t.ontwikkelingen) if (ontwPast(t, o, f, zoek, as)) for (const w of waarde(t, o)) uit[w] = (uit[w] ?? 0) + 1;
  }
  return uit;
}

// ── Sorteren ──────────────────────────────────────────────────────────────

export type Sortering = "score" | "bronnen" | "ontwikkelingen" | "beweging" | "urgentie" | "az";
export const SORTERINGEN: { waarde: Sortering; label: string; uitleg: string }[] = [
  { waarde: "score", label: "Standaard", uitleg: "Acuut, impact, omvang, beweging en werkvloer samen." },
  { waarde: "bronnen", label: "Meeste bronnen", uitleg: "Hoeveel verschillende afzenders de trend dragen." },
  { waarde: "ontwikkelingen", label: "Meeste ontwikkelingen", uitleg: "Hoeveel concrete ontwikkelingen onder de trend hangen." },
  { waarde: "beweging", label: "Beweging", uitleg: "Waar het aandeel recente bronnen het grootst is." },
  { waarde: "urgentie", label: "Urgentie", uitleg: "Hoe snel het speelt: waarom-nu-bewijs en regels met een datum." },
  { waarde: "az", label: "A–Z", uitleg: "Op titel, alfabetisch." },
];

const nul = (x: number | null | undefined) => x ?? -1;
const SLEUTEL: Record<Exclude<Sortering, "az">, (t: TrendlaagTrend) => number> = {
  score: (t) => nul(t.score),
  bronnen: (t) => nul(t.bronnen),
  ontwikkelingen: (t) => t.ontwikkelingen.length,
  beweging: (t) => nul(t.beweging),
  urgentie: (t) => nul(t.urgentie),
};

/** Sorteert een kopie; bij gelijke stand beslist de score, daarna de plek. */
export function sorteer(trends: readonly TrendlaagTrend[], s: Sortering): TrendlaagTrend[] {
  const kopie = [...trends];
  if (s === "az") return kopie.sort((a, b) => a.titel.localeCompare(b.titel, "nl"));
  const k = SLEUTEL[s];
  return kopie.sort((a, b) => k(b) - k(a) || nul(b.score) - nul(a.score) || a.plek - b.plek);
}
